import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Session } from "@supabase/supabase-js";
import { useTrialStatus } from "@/hooks/useTrialStatus";
import { useIsAdmin } from "@/hooks/useIsAdmin";

export const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const { isTrialExpired, hasActiveSubscription, isLoading: trialLoading } = useTrialStatus();
  const { isAdmin, isLoading: adminLoading } = useIsAdmin();

  useEffect(() => {
    const REVIEW_EMAIL = "review@quotesendgo.com";

    // Set up auth state listener
    let hasLoggedReviewLogin = false;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setLoading(false);

        // Log review account logins — only on initial SIGNED_IN, not token refreshes
        if (
          event === "SIGNED_IN" &&
          !hasLoggedReviewLogin &&
          session?.user?.email?.toLowerCase() === REVIEW_EMAIL
        ) {
          hasLoggedReviewLogin = true;
          supabase.functions.invoke("log-review-login").catch(() => {});
        }
      }
    );

    // Check for existing session and validate the user still exists
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session) {
        // Validate that the user in the JWT actually exists
        const { data: { user }, error } = await supabase.auth.getUser();
        if (error || !user) {
          // Stale session — user was deleted. Sign out to clear it.
          console.warn("Stale session detected — user no longer exists. Signing out.");
          await supabase.auth.signOut();
          setSession(null);
          setLoading(false);
          return;
        }
      }
      setSession(session);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  if (loading || trialLoading || adminLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/auth" replace />;
  }

  // Allow access to settings page even if trial expired
  const isSettingsPage = location.pathname.startsWith("/settings") || location.pathname === "/profile";
  
  // Redirect to settings if trial expired and not on settings/profile page
  // Admins bypass this check entirely
  if (isTrialExpired && !hasActiveSubscription && !isSettingsPage && !isAdmin) {
    return <Navigate to="/settings" replace />;
  }

  return <>{children}</>;
};
